import { useState, useEffect } from 'react';
import { User, Lock, Loader2, Check, AlertCircle, PhoneCall } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { resolveRoleKey, type DashboardRoleKey } from '@/pages/dashboard/roleLayouts';

interface ProfileData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  avatar?: string;
  role?: { name: string; displayName: string };
  department?: { name: string } | null;
  tenantId?: string;
  createdAt?: string;
}

export function ProfilePage() {
  const { user, fetchMe } = useAuthStore();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);

  // Profile form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Call Routing (ChatKart handoff M2 — Apr 24, 2026)
  // Dial-able PSTN number Exotel bridges to when a WhatsApp call comes in.
  const [dialablePhone, setDialablePhone] = useState('');
  const [dialablePhoneLoaded, setDialablePhoneLoaded] = useState(false);
  const [savingDialable, setSavingDialable] = useState(false);
  const [dialableMsg, setDialableMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadProfile();
    loadPresence();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await api.get('/auth/me');
      const data = res.data.data;
      setProfile(data);
      setFirstName(data.firstName || '');
      setLastName(data.lastName || '');
      setPhone(data.phone || '');
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  const loadPresence = async () => {
    try {
      const res = await api.get('/presence/me');
      const d = res?.data?.data;
      if (d && typeof d.dialablePhone === 'string') {
        setDialablePhone(d.dialablePhone);
      }
    } catch {
      // 400 when user has no tenantId; silently ignore — the card hides via
      // the role/tenant gate below.
    } finally {
      setDialablePhoneLoaded(true);
    }
  };

  const E164_REGEX = /^\+[1-9]\d{7,14}$/;

  const handleSaveDialablePhone = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = dialablePhone.trim();
    setDialableMsg(null);
    if (!E164_REGEX.test(value)) {
      setDialableMsg({
        type: 'error',
        text: 'Phone must be E.164 format (e.g. +919876543210).',
      });
      return;
    }
    setSavingDialable(true);
    try {
      await api.post('/presence/dialable-phone', { phone: value });
      setDialableMsg({ type: 'success', text: 'Dial-able number saved.' });
    } catch (err: any) {
      setDialableMsg({
        type: 'error',
        text: err?.response?.data?.error || 'Failed to save dial-able number.',
      });
    } finally {
      setSavingDialable(false);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setProfileMsg(null);
    try {
      await api.patch('/auth/profile', { firstName, lastName, phone });
      setProfileMsg({ type: 'success', text: 'Profile updated successfully' });
      // Refresh the global auth store so Header updates
      await fetchMe();
      await loadProfile();
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: err.response?.data?.error || 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMsg(null);

    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMsg({ type: 'error', text: 'New password must be at least 8 characters' });
      return;
    }

    setChangingPassword(true);
    try {
      await api.patch('/auth/change-password', { currentPassword, newPassword });
      setPasswordMsg({ type: 'success', text: 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordMsg({ type: 'error', text: err.response?.data?.error || 'Failed to change password' });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  const initials = `${profile?.firstName?.[0] || ''}${profile?.lastName?.[0] || ''}`.toUpperCase();

  // Call Routing card gating — same roles as the TopBar chip + admin.
  // Matches CallAvailabilityChip's CALL_ROLE_KEYS so the UX is consistent:
  // if a user sees the chip, they see the card; if they don't see the chip,
  // they don't see the card either.
  const CALL_ROLE_KEYS = new Set<DashboardRoleKey>([
    'OWNER',
    'SALES_MANAGER',
    'SALES_EXECUTIVE',
    'TELECALLER',
    'SERVICE_MANAGER',
    'SERVICE_TECHNICIAN',
    'FIELD_PARTNER',
  ]);
  const userRoleName = (user?.role?.name ?? '').toLowerCase();
  const isAdmin = userRoleName === 'admin';
  const callRoleKey = resolveRoleKey(user as never);
  const showCallRouting =
    !!user?.tenantId && (isAdmin || CALL_ROLE_KEYS.has(callRoleKey));

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">My Profile</h1>
        <p className="mt-1 text-sm text-gray-500">Manage your personal information and password</p>
      </div>

      {/* Profile Overview Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--saffron-50)] text-xl font-bold text-[var(--ink-0)]">
            {initials}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {profile?.firstName} {profile?.lastName}
            </h2>
            <p className="text-sm text-gray-500">{profile?.email}</p>
            <div className="mt-1 flex items-center gap-3">
              {profile?.role && (
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                  {profile.role.displayName}
                </span>
              )}
              {profile?.department && (
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                  {profile.department.name}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <User className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-slate-900">Edit Profile</h3>
        </div>

        {profileMsg && (
          <div className={`mb-4 flex items-center gap-2 rounded-lg px-4 py-3 text-sm ${
            profileMsg.type === 'success'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}>
            {profileMsg.type === 'success' ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            {profileMsg.text}
          </div>
        )}

        <form onSubmit={handleSaveProfile} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-500">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-500">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500">Email</label>
            <input
              type="email"
              value={profile?.email || ''}
              disabled
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-400 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500">Phone</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 9876543210"
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>

      {/* Call Routing Card (ChatKart handoff M2 — Apr 24, 2026) ────────── */}
      {showCallRouting && (
        <div
          className="rounded-lg border p-6"
          style={{
            background: 'var(--ivory-1)',
            borderColor: 'var(--line)',
          }}
        >
          <div className="mb-4 flex items-center gap-2">
            <PhoneCall className="h-5 w-5" style={{ color: 'var(--saffron-600)' }} />
            <h3
              className="text-lg font-semibold"
              style={{ color: 'var(--ink-0)' }}
            >
              Call Routing
            </h3>
          </div>
          <p
            className="mb-4 text-sm"
            style={{ color: 'var(--ink-2)' }}
          >
            WhatsApp calls ringing to AURA will be bridged to this number via
            Exotel. Must be E.164 (e.g. +919876543210).
          </p>

          {dialableMsg && (
            <div
              className="mb-4 flex items-center gap-2 rounded-lg px-4 py-3 text-sm"
              style={{
                background:
                  dialableMsg.type === 'success' ? 'var(--forest-50)' : 'var(--danger-bg)',
                color:
                  dialableMsg.type === 'success' ? 'var(--forest-600)' : 'var(--danger)',
                border: `1px solid ${
                  dialableMsg.type === 'success' ? 'var(--forest-100)' : '#EFCFCA'
                }`,
              }}
            >
              {dialableMsg.type === 'success' ? (
                <Check className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              {dialableMsg.text}
            </div>
          )}

          <form onSubmit={handleSaveDialablePhone} className="space-y-4">
            <div>
              <label
                className="mb-1 block text-sm font-medium"
                style={{ color: 'var(--ink-2)' }}
              >
                Dial-able mobile number
              </label>
              <input
                type="tel"
                inputMode="tel"
                value={dialablePhone}
                onChange={(e) => setDialablePhone(e.target.value)}
                placeholder="+919876543210"
                pattern="^\+[1-9]\d{7,14}$"
                disabled={!dialablePhoneLoaded}
                className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
                style={{
                  background: 'var(--ivory-0)',
                  borderColor: 'var(--line)',
                  color: 'var(--ink-0)',
                }}
              />
              <div
                className="mt-1 text-[11px]"
                style={{ color: 'var(--ink-3)' }}
              >
                Required before you can set yourself Available in the top bar.
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={savingDialable || !dialablePhoneLoaded}
                className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium"
                style={{
                  background: 'var(--saffron-600)',
                  color: '#FEFCF7',
                  opacity: savingDialable || !dialablePhoneLoaded ? 0.6 : 1,
                  cursor: savingDialable ? 'wait' : 'pointer',
                }}
              >
                {savingDialable && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Number
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Change Password Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <Lock className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-slate-900">Change Password</h3>
        </div>

        {passwordMsg && (
          <div className={`mb-4 flex items-center gap-2 rounded-lg px-4 py-3 text-sm ${
            passwordMsg.type === 'success'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}>
            {passwordMsg.type === 'success' ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            {passwordMsg.text}
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-500">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-500">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={changingPassword}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
            >
              {changingPassword && <Loader2 className="h-4 w-4 animate-spin" />}
              Change Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
